import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { formatFileSize } from "@/lib/utils";
import { useGaContext } from "@/hooks/useGaContext";
import {
  Upload,
  FileText,
  Trash2,
  Download,
  Plus,
  X,
  FolderOpen,
  Tag,
} from "lucide-react";

function mimeIcon(mime: string) {
  if (!mime) return <FileText className="w-5 h-5 text-muted-foreground" />;
  if (mime.includes("pdf")) return <FileText className="w-5 h-5 text-red-500 dark:text-red-400" />;
  if (mime.includes("word") || mime.includes("document"))
    return <FileText className="w-5 h-5 text-blue-500 dark:text-blue-400" />;
  if (mime.includes("sheet") || mime.includes("excel"))
    return <FileText className="w-5 h-5 text-green-500 dark:text-green-400" />;
  return <FileText className="w-5 h-5 text-muted-foreground" />;
}

export default function GADocuments() {
  const { gaId } = useGaContext();
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: "",
    description: "",
    tagInput: "",
    tags: [] as string[],
  });
  const [filePayload, setFilePayload] = useState<{
    data: string;
    mime: string;
    size: number;
    originalName: string;
  } | null>(null);

  const { data: documents = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/ga", gaId, "documents"],
    queryFn: async () => {
      const res = await fetch(`/api/ga/${gaId}/documents`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!gaId,
  });

  const uploadMutation = useMutation({
    mutationFn: async (payload: any) =>
      apiRequest("POST", `/api/ga/${gaId}/documents`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "documents"] });
      toast({ title: "Document uploaded", description: "Now visible to your broker network." });
      setShowUpload(false);
      setUploadForm({ name: "", description: "", tagInput: "", tags: [] });
      setFilePayload(null);
    },
    onError: () =>
      toast({ title: "Upload failed", description: "Please try again.", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (docId: string) =>
      apiRequest("DELETE", `/api/ga/${gaId}/documents/${docId}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ga", gaId, "documents"] });
      toast({ title: "Document removed" });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(",")[1];
      setFilePayload({ data: base64, mime: file.type, size: file.size, originalName: file.name });
      if (!uploadForm.name) setUploadForm((f) => ({ ...f, name: file.name.replace(/\.[^.]+$/, "") }));
    };
    reader.readAsDataURL(file);
  };

  const addTag = () => {
    const tag = uploadForm.tagInput.trim();
    if (tag && !uploadForm.tags.includes(tag)) {
      setUploadForm((f) => ({ ...f, tags: [...f.tags, tag], tagInput: "" }));
    }
  };

  const removeTag = (tag: string) => {
    setUploadForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }));
  };

  const handleSubmit = () => {
    if (!uploadForm.name.trim()) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    if (!filePayload) {
      toast({ title: "Please select a file", variant: "destructive" });
      return;
    }
    uploadMutation.mutate({
      name: uploadForm.name.trim(),
      description: uploadForm.description.trim() || null,
      tags: uploadForm.tags,
      fileData: filePayload.data,
      mimeType: filePayload.mime,
      sizeBytes: filePayload.size,
      uploadedBy: "GA Admin",
    });
  };

  const handleDownload = (doc: any) => {
    if (!doc.fileData) return;
    const byteChars = atob(doc.fileData);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArray], { type: doc.mimeType || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Documents & Resources</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Files shared with your broker network — visible to all linked brokers
          </p>
        </div>
        <Button onClick={() => setShowUpload(true)} className="gap-2">
          <Plus className="w-4 h-4" /> Upload Document
        </Button>
      </div>

      {/* Upload form */}
      {showUpload && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-950/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Upload className="w-4 h-4" /> Upload New Document
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File picker */}
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-600 transition-colors bg-card"
              onClick={() => fileRef.current?.click()}
            >
              {filePayload ? (
                <div className="flex items-center justify-center gap-2 text-sm text-foreground">
                  <FileText className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                  <span className="font-medium">{filePayload.originalName}</span>
                  <span className="text-muted-foreground">({formatFileSize(filePayload.size, { emptyText: "—" })})</span>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Click to choose a file</p>
                  <p className="text-xs text-muted-foreground mt-1">PDF, Word, Excel, images — max 5 MB</p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
                onChange={handleFileChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Document Name</Label>
                <Input
                  placeholder="e.g. 2025 Enrollment Guide"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add tag and press Enter"
                    value={uploadForm.tagInput}
                    onChange={(e) => setUploadForm((f) => ({ ...f, tagInput: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addTag}>
                    <Tag className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {uploadForm.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {uploadForm.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded px-2 py-0.5"
                      >
                        {tag}
                        <button onClick={() => removeTag(tag)}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Brief description of this document..."
                rows={2}
                value={uploadForm.description}
                onChange={(e) => setUploadForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={() => {
                  setShowUpload(false);
                  setFilePayload(null);
                  setUploadForm({ name: "", description: "", tagInput: "", tags: [] });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={uploadMutation.isPending}>
                {uploadMutation.isPending ? "Uploading…" : "Upload"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document list */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No documents yet</p>
              <p className="text-xs mt-1">Upload your first document to share with your broker network</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted border-b border-border">
                <tr className="text-xs text-muted-foreground uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Document</th>
                  <th className="text-left px-4 py-3">Tags</th>
                  <th className="text-left px-4 py-3">Size</th>
                  <th className="text-left px-4 py-3">Uploaded</th>
                  <th className="text-left px-4 py-3">By</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {documents.map((doc: any) => (
                  <tr key={doc.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {mimeIcon(doc.mimeType)}
                        <div>
                          <p className="font-medium text-foreground">{doc.name}</p>
                          {doc.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 max-w-xs truncate">{doc.description}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(doc.tags || []).map((tag: string) => (
                          <Badge key={tag} variant="secondary" className="text-xs px-1.5 py-0">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{formatFileSize(doc.sizeBytes, { emptyText: "—" })}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{doc.uploadedBy || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {doc.fileData && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs gap-1"
                            onClick={() => handleDownload(doc)}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950"
                          onClick={() => deleteMutation.mutate(doc.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
